import React, { useState, useEffect } from 'react';
import { useLocalStorage } from 'react-use';

// Utils
import { renderKr } from 'utils/renderingService';
import { mediaQueries, fromTabletMediaQuery, fromTablethoriMediaQuery } from 'utils/MediaQueriesDeprecated';
import { FAQPageJsonLd } from 'next-seo';
import { Routes } from 'utils/routes';
import { useMatomo } from '@jonkoops/matomo-tracker-react';

// FAQ texts
import { klatpensionLandingPageFAQEntries } from 'utils/texts';

// Components
import { Hero, PenlySeo, Spacer, Button, Slider, ContentBoxContainer, ContentBoxText, ContentBoxFigure, PensionKlatpensionGraph, PensionKlatpensionCalculationInfoDialog, PageFAQ, VideoPlayer,KlatpensionArticlesPreviews } from 'components';

// Next
import { useRouter } from 'next/router';

// Calculations
import { pensionAnalysisKlatpension } from '../../src/api/neway';

export default function Klatpension() {
    const [, setKlatpension] = useLocalStorage('klatpension');
    const title = 'Gebyrer æder din klatpension - Sæt en stopper for det med Penly';
    const description = '26% af Penlys brugere har i gennemsnit 15.000 kr. stående på klatpensioner. Penge der bliver spist op af gebyrer, hvis ikke du gør noget ved dem.';
    const image = { url: '/static/gfx/content/articles/penly-article-10-rene-holten-poulsen-action-hero.jpg' };
    const fromTablet = mediaQueries(fromTabletMediaQuery);
    const router = useRouter();
    const { trackEvent } = useMatomo();

    /* #region Calculation state */

    const [pensionState, setPensionState] = useState(null);
    const [result, setResult] = useState(null);

    /* #endregion */

    /* #region Config values */

    const minAge = 20;
    const maxAge = 65;
    const minKlatpensionValue = 1000;
    const maxKlatpensionValue = 25000;

    /* #endregion */

    /* #region Local state handling */

    const [age, setAge] = useState(45);
    const [pensionValue, setPensionValue] = useState(10000);
    const [showCalculationInfo, setShowCalculationInfo] = useState(false);

    const handleInputBlur = async () => {
        const updatedState = { ...pensionState };
        updatedState.age = parseInt(age, 0) ?? null;
        updatedState.pensionValue = parseFloat(pensionValue) ?? null;
        setPensionState(updatedState);
        const res = await pensionAnalysisKlatpension({ age, value: pensionValue });
        setResult(res);
    };

    function ageText(value) {
        return `${value} år`;
    }

    const ageMarks = [
        {
            value: minAge,
            label: `${minAge} år`,
        },
        {
            value: maxAge,
            label: `${maxAge} år`,
        },
    ];

    function pensionValueText(value) {
        return renderKr(value);
    }

    const pensionValueMarks = [
        {
            value: minKlatpensionValue,
            label: renderKr(minKlatpensionValue),
        },
        {
            value: maxKlatpensionValue,
            label: renderKr(maxKlatpensionValue),
        },
    ];

    const toggleCalculationInfo = () => {
        setShowCalculationInfo(!showCalculationInfo);
    };

    /* #endregion */

    const gotoPartner = () => {
        trackEvent({
            category: 'KlatPension',
            action: 'NORDInvestmentsReferal_Landingpage',
        });
        window.open('https://signup.nord.investments/henvisning?referral_token=Penly');
    };

    const gotoNordnet = () => {
        trackEvent({
            category: 'KlatPension',
            action: 'NordnetReferal_Landingpage',
        });
        window.open('https://www.nordnet.dk/dk/tjenester/pension/flyt-pension?utm_source=penly&utm_medium=referral&utm_campaign=flyt-pension&utm_term=organic');
    };

    // Initialize at load
    useEffect(() => {
        setKlatpension(true);
    }, []);

    return (
        <>
            <FAQPageJsonLd mainEntity={ klatpensionLandingPageFAQEntries } />

            <PenlySeo title={ title } description={ description } image={ image } />

            <Hero full={ true } overlay={ true } figure={ image.url }>
                <section className="title">
                    <h1>
                        RENÉ HOLTEN POULSEN<br></br>
                        FANDT 8.600 KR. <span className="color-highlight"><br></br>
                            I en klatpension, han havde glemt alt om</span>
                    </h1>

                    <Spacer />
                    <Button size="large" onClick={ () => router.push(Routes.PensionCheck.Start) }>Se her om du har penge til gode</Button>
                </section>
                <div className="taenkBadge"></div>
            </Hero>

            <Spacer size="large" />
            <VideoPlayer id="video" videoId="A5hwMt0rO6I" thumbnail="/static/gfx/content/penly-videothumbnail-A5hwMt0rO6I.png"  autoPlay={ false } />
            <Spacer size="small" />

            <article className="klatPension twocol4060">
                <section>
                    <div>
                        <h2>Se, hvor hurtigt din klatpension forsvinder</h2>
                        <p>Indstil din alder og din klatpensions værdi, og se hvor hurtigt din klatpension bliver spist gebyrer.</p>
                        <div className="formgroups">
                            <div className="formgroup">
                                <Slider
                                    label="Din alder"
                                    step={ 1 }
                                    min={ minAge }
                                    max={ maxAge }
                                    value={ age }
                                    getAriaValueText={ ageText }
                                    valueLabelFormat={ ageText }
                                    marks={ ageMarks }
                                    onChangeCommitted={ handleInputBlur }
                                    onChange={ (e, value) => setAge(value) }
                                />

                                <Slider
                                    label="Klatpension værdi"
                                    step={ 500 }
                                    min={ minKlatpensionValue }
                                    max={ maxKlatpensionValue }
                                    value={ pensionValue }
                                    getAriaValueText={ pensionValueText }
                                    valueLabelFormat={ pensionValueText }
                                    marks={ pensionValueMarks }
                                    onChangeCommitted={ handleInputBlur }
                                    onChange={ (e, value) => setPensionValue(value) }
                                />
                            </div>
                        </div>
                    </div>
                </section>
                <section>
                    <div>
                        <PensionKlatpensionGraph data={ result } firstTitle="Hvis du intet gør" secondaryTitle="Hvis du redder din klatpension"></PensionKlatpensionGraph>
                        <div style={{ textAlign: 'center', paddingTop: '20px' }}>
                            <a onClick={ () => toggleCalculationInfo() } className="infoAnchor">
                                Hvordan har vi beregnet det?
                            </a>
                        </div>
                    </div>
                </section>
            </article>

            <div>
                <Button onClick={ () => router.push(Routes.PensionCheck.Start) }>OPRET PROFIL OG

                    TJEK FOR KLATPENSIONER NU</Button>
            </div>

            <Spacer />

            { showCalculationInfo && <PensionKlatpensionCalculationInfoDialog toggleDialog={ toggleCalculationInfo } /> }

            <Spacer size="small" />

            { /* <ContentBoxContainer className="brbr brtl mobileflip" figure="/static/gfx/content/penly-testimonial-anders-andersen.jpg">
                <ContentBoxText className="overlay color-secondary">
                    <h3>
                        &quot; Det viste sig at jeg havde <span className="color-highlight">fire</span> klatpensioner.
                    </h3>
                    <p>
                        Med hjælp fra Penly og en uvildig rådgiver fik jeg styr på det og
                        kan nu forvente at blive xx kr. rigere, når jeg går på pension.
                    </p>
                    <p className="right">Anders Andersen</p>
                </ContentBoxText>
                <ContentBoxFigure className="servicelogo" figure="/static/gfx/PenlyStars.svg"></ContentBoxFigure>
            </ContentBoxContainer>

            <Spacer size="large" /> */ }

            <ContentBoxContainer>
                <ContentBoxFigure figure="/static/gfx/content/penly-content-33.jpg" className="background-primary brtl servicelogoinline"></ContentBoxFigure>
                <ContentBoxText className="background-primary brbr">
                    <h3>Så nemt er det at redde dine klatpensioner</h3>
                    <ol className="large">
                        <li>
                            <h5>Overblik</h5>
                            Find ud af, om du har klatpensioner ved at tage et Penly PensionsTjek.
                        </li>
                        <li>
                            <h5>Afklaring</h5>
                            Book en uforpligtende gennemgang og få gennemgået din pensionsanalyse.                        </li>
                        <li>
                            <h5>Tag action</h5>
                            Flyt din klatpension, og se den vokse.
                        </li>
                    </ol>
                    <Button onClick={ () => router.push(Routes.PensionCheck.Start) } color="white" size={ fromTablet ? 'large' : 'medium' }>
                        OPRET PROFIL OG
                        TJEK FOR KLATPENSIONER NU
                    </Button>
                </ContentBoxText>
            </ContentBoxContainer>

            <Spacer size="large" />

            { /* <ContentBoxContainer>
                <ContentBoxFigure figure="/static/gfx/content/penly-content-nord-investments.jpg" className="background-affiliate-nordinvestments brtl servicelogoinline">
                    <img src="/static/gfx/content/nord-investments-logo.svg" alt="NORD Investments" />
                </ContentBoxFigure>
                <ContentBoxText className="background-affiliate-nordinvestments brbr">
                    <h3>Nem håndtering af din klatpension</h3>
                    <p>Er din klatpension på min. 20.000 kr., kan du via vores samarbejdspartner NORD Investments nemt få investeret den.</p>
                    <p>
                        NORD Investments håndterer alt fra flytning til investering. Omkostningerne er lave, og hvis du flytter en pension på mere end 50.000 kr., belønner NORD investments dig med 250
                        kr.
                    </p>

                    <Button color="white" onClick={ () => gotoPartner() }>
                        Læs mere om tilbuddet
                    </Button>
                </ContentBoxText>
            </ContentBoxContainer> */ }

            <ContentBoxContainer className="affiliate-nordnet">
                <ContentBoxFigure className="brtl servicelogoinline"></ContentBoxFigure>
                <ContentBoxText className="brbr">
                    <h3>Flyt din pension til Nordnet</h3>

                    <p>
                        Hos Nordnet får du en pensionsløsning, som giver dig mest muligt at leve for. Du vælger selv, hvordan du vil investere din pensionsopsparing – med lave omkostninger og et bredt
                        udvalg af værdipapirer fra hele verden. Med Nordnets digitale flytte-guide kan du nemt se, hvilke ordninger du kan flytte.
                    </p>
                    <ul>
                        <li>Lave omkostninger fra 0,2 %.</li>
                        <li>Giver dig mulighed for at sammenligne din Nordnet-pension med dine andre pensioner og se, hvilke der performer bedst.</li>
                        <li>Lad Nordnet stå for investeringen med Nordnet One. En professionel sammensat og forvaltet investeringsløsning.</li>
                    </ul>

                    <Button color="white" onClick={ () => gotoNordnet() }>
                        Læs mere om tilbuddet
                    </Button>
                </ContentBoxText>
            </ContentBoxContainer>

            <Spacer size="large" />

            <KlatpensionArticlesPreviews></KlatpensionArticlesPreviews>
            <Spacer size="large" />

            <PageFAQ title="FAQ om klatpension" faqEntries={ klatpensionLandingPageFAQEntries } />

            <Spacer />

            <ContentBoxContainer>
                <ContentBoxText className="background-highlight brtl">
                    <h3>Undgå at dine penge bliver spist af gebyrer</h3>
                    <Spacer />
                    <div className="center">
                        <Button href={ Routes.PensionCheck.Start } size="large">
                            OPRET PROFIL OG
                            TJEK FOR KLATPENSIONER NU
                        </Button>
                    </div>
                </ContentBoxText>
                <ContentBoxText className="background-primary brbr">
                    <h3>Har du spørgsmål?</h3>
                    <div className="large">
                        <h6>Kontakt vores kundeservice</h6>
                        Telefon: <a href="tel:+4593838372">+45 93 83 83 72</a>
                        <br />
                        Du kan ringe til os tirsdag og torsdag mellem 10.00 og 14.00.
                        <br />
                        <br />
                        E-mail: <a href="mailto:kontakt@penly.dk">kontakt@penly.dk</a>
                    </div>
                </ContentBoxText>
            </ContentBoxContainer>

            <Spacer size="large" />
        </>
    );
}
